import React, { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import DataTable from "react-data-table-component";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL } from "../../config";
import { reportTableStyles } from "../Reports/reportTableStyles";

const AssetReport = () => {
  const [filters, setFilters] = useState({
    dept_id: "0",
    block_id: "0",
    category_id: "0",
    action_type: "Fetch",
  });

  const [departments, setDepartments] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [categories, setCategories] = useState([]);

  const [records, setRecords] = useState([]);
  const [filterText, setFilterText] = useState("");

  /* ================================
   * LOAD MASTER DATA
   * ================================
   */
  const loadMasters = async () => {
    try {
      const d = await axios.get(API_BASE_URL + "/departments");
      const b = await axios.get(API_BASE_URL + "/blocks");
      const c = await axios.get(API_BASE_URL + "/categories");

      setDepartments(d.data.data || d.data);
      setBlocks(b.data.data || b.data);
      setCategories(c.data.data || c.data);
    } catch (error) {
      toast.error("Failed to load master data");
    }
  };

  useEffect(() => {
    loadMasters();
  }, []);

  /* ================================
   * HANDLE FILTER INPUT
   * ================================
   */
  const handleSelectChange = (name, value) => {
    setFilters({ ...filters, [name]: value });
  };

  /* ================================
   * FETCH REPORT
   * ================================
   */
const handleSubmit = async (e) => {
  e.preventDefault();

  try {
    const res = await axios.get(API_BASE_URL + "/asset_report_list", {
      params: filters,
    });

    const formatted = res.data.data.map((row) => ({
      asset_name: row[0],
      category: row[1],
      asset_code: row[2],
      location: row[3],
      status: row[4],
      department: row[5],
      unit_price: row[6],
    }));

    setRecords(formatted);

    toast.success("Asset Report Loaded", { position: "bottom-right" });

  } catch {
    toast.error("Failed to load report", { position: "bottom-right" });
  }
};

  /* ================================
   * DATATABLE COLUMNS
   * ================================
   */
  const columns = [
    { name: "Asset Name", selector: (row) => row.asset_name, sortable: true },
    { name: "Category", selector: (row) => row.category, sortable: true },
    { name: "Asset Code", selector: (row) => row.asset_code, sortable: true },
    { name: "Location", selector: (row) => row.location, sortable: true },
    { name: "Status", selector: (row) => row.status, sortable: true },
    { name: "Department", selector: (row) => row.department, sortable: true },
    { name: "Unit Price", selector: (row) => row.unit_price, sortable: true },
  ];

  /* ================================
   * SEARCHABLE FILTER
   * ================================
   */
  const filteredData = records.filter((item) =>
    `${item.asset_name} ${item.category} ${item.asset_code} ${item.department} ${item.location}`
      .toLowerCase()
      .includes(filterText.toLowerCase())
  );

  

  /* ================================
   * OPTIONS FOR SELECT DROPDOWNS
   * ================================
   */
const downloadCSV = () => {
  if (!records.length) {
    toast.error("No records to download");
    return;
  }

  const header = [
    "Asset Name",
    "Category",
    "Asset Code",
    "Location",
    "Status",
    "Department",
    "Unit Price"
  ];

  // Clean CSV rows – remove commas/linebreaks/spaces
  const clean = (value) => {
    if (value === null || value === undefined) return "";
    return String(value).replace(/[\n\r,]+/g, " ").trim();
  };

  const rows = records.map((r) => [
    clean(r.asset_name),
    clean(r.category),
    clean(r.asset_code),
    clean(r.location),
    clean(r.status),
    clean(r.department),
    clean(r.unit_price)
  ]);

  // Create CSV text
  let csvContent =
    "data:text/csv;charset=utf-8," +
    [header, ...rows].map((e) => e.join(",")).join("\n");

  // Download
  const link = document.createElement("a");
  link.setAttribute("href", encodeURI(csvContent));
  link.setAttribute("download", "Asset_Report.csv");
  document.body.appendChild(link);
  link.click();
};

  const deptOptions = [
    { label: "All Department", value: "0" },
    ...departments.map((d) => ({
      label: d.department_name,
      value: d.department_id,
    })),
  ];

  const blockOptions = [
    { label: "All Block", value: "0" },
    ...blocks.map((b) => ({
      label: b.block_name,
      value: b.block_id,
    })),
  ];

  const categoryOptions = [
    { label: "All Category", value: "0" },
    ...categories.map((c) => ({
      label: c.category_name,
      value: c.assets_category_id,
    })),
  ];

  return (
    <div className="container mt-4">

      <ToastContainer />

      {/* HEADER MENU */}
      <div className="mb-3 d-flex gap-2">
        <a href="/asset-report" className="btn btn-info btn-sm">Asset Report</a>
        <a href="/workorder" className="btn btn-outline-secondary btn-sm">Workorder Report</a>
        <a href="/consolidated" className="btn btn-outline-secondary btn-sm">
          Workorder Consolidated Report
        </a>
      </div>

      {/* FILTER CARD */}
      <div className="card mb-4 shadow-sm">
        <div className="card-header">
          <h6 className="mb-0">Asset Report Filter</h6>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row g-3">

              {/* Department */}
              <div className="col-md-3">
                <label>Department</label>
                <Select
                  options={deptOptions}
                  value={deptOptions.find((d) => d.value === filters.dept_id)}
                  onChange={(val) => handleSelectChange("dept_id", val.value)}
                />
              </div>

              {/* Block */}
              <div className="col-md-3">
                <label>Block</label>
                <Select
                  options={blockOptions}
                  value={blockOptions.find((b) => b.value === filters.block_id)}
                  onChange={(val) => handleSelectChange("block_id", val.value)}
                />
              </div>

              {/* Category */}
              <div className="col-md-3">
                <label>Category</label>
                <Select
                  options={categoryOptions}
                  value={categoryOptions.find((c) => c.value === filters.category_id)}
                  onChange={(val) => handleSelectChange("category_id", val.value)}
                />
              </div>

              {/* Submit */}
              <div className="col-md-2 d-flex align-items-end">
                <button className="btn btn-primary w-100">Get</button>
              </div>

            </div>
          </form>
        </div>
      </div>

      {/* DATA TABLE */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">

  {/* CSV Button */}
  <button
    className="btn btn-outline-secondary btn-sm"
    onClick={downloadCSV}
  >
    CSV
  </button>

  {/* Search Box */}
  <input
    type="text"
    className="form-control form-control-sm"
    placeholder="Search..."
    style={{ width: "200px" }}
    value={filterText}
    onChange={(e) => setFilterText(e.target.value)}
  />
</div>


        <div className="card-body">

          <DataTable
            columns={columns}
            data={filteredData}
            pagination
            striped
            highlightOnHover
            fixedHeader
            fixedHeaderScrollHeight="60vh"
            customStyles={reportTableStyles}
          />

        </div>
      </div>
    </div>
  );
};

export default AssetReport;
